import { Select } from "@mindlercare/ui-web";
import CONSTANTS from "core/constants";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMindlerApi } from "api/apiv2";
import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQuery } = ReactQueryV5;
import { Option } from "../patients/patientCard/patientData/patientTreatmentStatusModal/patientTreatmentStatusModal.types";

const groupedOptions = [
  {
    label: "TREATMENT_ACTIVE",
    options: Object.values(CONSTANTS.ACTIVE_TREATMENT).map((reason, index) => ({
      label: reason,
      value: index,
      type: reason,
    })),
  },
  {
    label: "TREATMENT_ENDED",
    options: Object.values(CONSTANTS.END_TREATMENT_REASONS).map(
      (reason, index) => ({
        label: reason,
        value: index + 1,
        type: reason,
      }),
    ),
  },
];

export const TreatmentStatusDropdDown = ({
  patientUserId,
  currentSelectedOption,
  onChange,
}: {
  patientUserId: string | number | null | undefined;
  currentSelectedOption: Option | null;
  onChange: (option: Option) => void;
}) => {
  const { t } = useTranslation();
  const apiv2 = useMindlerApi();
  const { data: patient } = useQuery(
    apiv2.patients.getByUserId.queryOptions(
      {
        patientUserId: Number(patientUserId),
      },
      {
        enabled: !!patientUserId,
      },
    ),
  );

  const isTreatmentActive =
    patient?.treatmentStatus?.lastTreatmentEvent?.type !==
    CONSTANTS.TREATMENT_EVENT_TYPE.TREATMENT_END;
  const currentEndTreatmentReason =
    patient?.treatmentStatus?.lastTreatmentEvent?.endTreatmentReason || null;

  const activeTreatmentDropdownOptions = useMemo(() => {
    return {
      label: t(CONSTANTS.ACTIVE_TREATMENT.IN_TREATMENT),
      value: 0,
      type: CONSTANTS.ACTIVE_TREATMENT.IN_TREATMENT,
    };
  }, [t]);
  const endTreatmentDropdownOptions = useMemo(
    () =>
      Object.values(CONSTANTS.END_TREATMENT_REASONS).map((reason, index) => ({
        label: t(reason),
        value: index + 1,
        type: reason,
      })),
    [t],
  );

  useEffect(() => {
    if (isTreatmentActive) {
      onChange(activeTreatmentDropdownOptions);
      return;
    }

    const endTreatmentReasonOption = endTreatmentDropdownOptions?.find(
      (x) => x.type === currentEndTreatmentReason,
    );
    if (endTreatmentReasonOption) {
      onChange(endTreatmentReasonOption);
    }
  }, [
    activeTreatmentDropdownOptions,
    currentEndTreatmentReason,
    endTreatmentDropdownOptions,
    isTreatmentActive,
    onChange,
  ]);

  return (
    <Select value={currentSelectedOption?.value?.toString()}>
      <Select.Trigger placeholder={t("TREATMENT_STATUS")}>
        {currentSelectedOption?.label ? t(currentSelectedOption.label) : null}
      </Select.Trigger>
      <Select.Options>
        {groupedOptions.map((group) => (
          <Select.OptionsGroup key={group.label} groupName={t(group.label)}>
            {group.options.map((option) => (
              <Select.Option
                key={option.value}
                value={option.value.toString()}
                onClick={() => onChange(option)}
              >
                {t(option.label)}
              </Select.Option>
            ))}
          </Select.OptionsGroup>
        ))}
      </Select.Options>
    </Select>
  );
};
